import { useState, useEffect, useCallback } from "react";
import { Grid, Button, FormControl, Select, MenuItem, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as PrinterIcon } from "images/svg/printer-grey.svg";
import { ReactComponent as FormatIcon } from "images/svg/document.svg";
import styles from "./styles.module.css";
import { BARCODE_PRINTER, PRINTER_TYPES } from "const";
import toast from "react-hot-toast";
import * as TerminalHelper from "helpers/TermialHelper";
import AppBridgeHelper from "helpers/AppBridgeHelper";

function BarcodePrintersPage() {
  const [printerType, setPrinterType] = useState("");
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [barcodePrinterFormat, setBarcodePrinterFormat] = useState("");
  const [barcodeColumn, setBarcodeColumn] = useState(2);
  const [customNote, setCustomNote] = useState("");

  useEffect(() => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const barcodePrinterType = terminalSettings.barcodePrinterType;
    const barcodePrinter = terminalSettings.printer;
    const barcodePrinterFormat = terminalSettings.barcodePrinterFormat;
    const barcodeCustomNote = terminalSettings.barcodeCustomNote;

    if (barcodePrinterType) setPrinterType(barcodePrinterType);
    if (barcodePrinter && barcodePrinterType !== PRINTER_TYPES.LAN) {
      setSelectedPrinter(barcodePrinter);
    }
    if (barcodePrinterFormat) setBarcodePrinterFormat(barcodePrinterFormat);
    if (barcodeCustomNote) setCustomNote(barcodeCustomNote);
  }, []);

  async function fetchPrintersFromAppBridge(printerType) {
    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) return null;

    if (printerType === PRINTER_TYPES.DIRECT_USB) {
      if (bridge.getDirectUsbPrinters) {
        const devicesJson = await bridge.getDirectUsbPrinters();
        const devicesList = JSON.parse(devicesJson);
        if (Array.isArray(devicesList)) {
          return devicesList.map((device, idx) => ({
            id: device.Path || `direct-usb-${idx}`,
            name: device.Name || "Direct USB Printer",
            path: device.Path,
          }));
        }
      }
    } else if (printerType === PRINTER_TYPES.USB) {
      if (bridge.getInstalledPrinters) {
        const printersJson = await bridge.getInstalledPrinters();
        const printersList = JSON.parse(printersJson);
        if (Array.isArray(printersList)) {
          return printersList.map((p, idx) => ({ id: `usb-${idx}`, name: p }));
        }
      }
    }
    return null;
  }

  const handleReloadPrinters = useCallback(
    async (isManual = false) => {
      if (!printerType) {
        if (isManual) toast.error("Please select printer type first");
        return;
      }

      if (printerType === PRINTER_TYPES.USB || printerType === PRINTER_TYPES.DIRECT_USB) {
        try {
          const formattedPrinters = await fetchPrintersFromAppBridge(printerType);
          if (formattedPrinters) {
            setPrinters(formattedPrinters);
            if (isManual) toast.success("Printers reloaded");
            return;
          }
        } catch (err) {
          console.error("Failed to load printers:", err);
          if (isManual) toast.error("Failed to load printers");
        }
      }

      let detectedPrinters = [];
      if (printerType === PRINTER_TYPES.USB) {
        detectedPrinters = [{ id: "USB1", name: "USB Thermal Printer" }];
      } else if (printerType === PRINTER_TYPES.LAN) {
        detectedPrinters = [{ id: "NET1", name: "Network Printer 01" }];
      } else if (printerType === PRINTER_TYPES.BLUETOOTH) {
        detectedPrinters = [{ id: "NET1", name: "Bluetooth Printer 01" }];
      }

      setPrinters(detectedPrinters);
    },
    [printerType],
  );

  useEffect(() => {
    if (printerType) {
      handleReloadPrinters(false);
    } else {
      setPrinters([]);
    }
  }, [printerType, handleReloadPrinters]);

  const handleSave = () => {
    if (!printerType || !selectedPrinter || !barcodePrinterFormat) {
      toast.error("Please select all required fields");
      return;
    }

    const success = TerminalHelper.saveTerminalSettings({
      barcodePrinterType: printerType,
      printer: selectedPrinter,
      barcodePrinterFormat,
      barcodeCustomNote: customNote,
    });

    if (success) {
      toast.success("Barcode printer settings saved successfully");
    } else {
      toast.error("Failed to save barcode printer settings");
    }
  };

  const handleTestPrint = () => {
    if (!selectedPrinter) {
      toast.error("Select a printer first");
      return;
    }
  };

  return (
    <Grid container className={styles.contentWrapper} rowGap={2}>
      {/* Printer Type */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <PrinterIcon />
          <h3 className={styles.subTitle}>Printer Type :</h3>
          <FormControl variant="standard" className={styles.selectWrapper}>
            <Select
              value={printerType}
              onChange={(e) => {
                setPrinterType(e.target.value);
                setPrinters([]);
                setSelectedPrinter("");
              }}
              displayEmpty
              disableUnderline
              IconComponent={ExpandMoreIcon}
              renderValue={(value) =>
                value ? (
                  <span className={styles.mainTitle}>
                    {value === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : value}
                  </span>
                ) : (
                  <span style={{ color: "#aaa" }}>Select Printer Type</span>
                )
              }
            >
              <MenuItem disabled value="">
                Select Printer Type
              </MenuItem>
              {BARCODE_PRINTER.type.map((type) => (
                <MenuItem key={type} value={type}>
                  {type === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Printer / IP Address Section */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <PrinterIcon />
          {printerType === PRINTER_TYPES.LAN ? (
            <>
              <h3 className={styles.subTitle}>IP Address :</h3>
              <TextField
                placeholder="Enter IP Address"
                value={selectedPrinter}
                onChange={(e) => setSelectedPrinter(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: 0,
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#2b2b2b",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                  },
                }}
              />
            </>
          ) : (
            <>
              <h3 className={styles.subTitle}>
                {printerType === PRINTER_TYPES.BLUETOOTH
                  ? "Bluetooth Printer :"
                  : printerType === PRINTER_TYPES.DIRECT_USB
                    ? "Direct USB Printer :"
                    : "USB Printer :"}
              </h3>
              <FormControl variant="standard" className={styles.selectWrapper}>
                <Select
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  displayEmpty
                  disableUnderline
                  IconComponent={ExpandMoreIcon}
                  renderValue={(value) => {
                    if (!value)
                      return (
                        <span style={{ color: "#aaa" }}>{printers.length ? "Select Printer" : "No Device Found"}</span>
                      );
                    if (value === "default") {
                      return (
                        <span className={styles.mainTitle}>
                          Default {printerType === PRINTER_TYPES.DIRECT_USB ? "USB Without Driver" : printerType}{" "}
                          Printer
                        </span>
                      );
                    }
                    const found = printers.find((p) => p.id === value || p.path === value || p.name === value);
                    if (found) return <span className={styles.mainTitle}>{found.name}</span>;

                    if (value.startsWith("\\\\?\\") || value.includes("#") || value.includes("&")) {
                      const match = value.match(/vid_([0-9a-fA-F]+)&pid_([0-9a-fA-F]+)/i);
                      if (match) {
                        const vid = match[1].toUpperCase();
                        const pid = match[2].toUpperCase();
                        const parts = value.split("#");
                        let sn = "";
                        if (parts.length > 2 && !parts[2].includes("&")) {
                          sn = parts[2];
                        }
                        return (
                          <span className={styles.mainTitle}>
                            USB Printer (VID: {vid}, PID: {pid}
                            {sn ? `, SN: ${sn}` : ""})
                          </span>
                        );
                      }
                      return <span className={styles.mainTitle}>USB Printer Device</span>;
                    }
                    return <span className={styles.mainTitle}>{value}</span>;
                  }}
                >
                  <MenuItem disabled value="">
                    {printers.length ? "Select Printer" : "No Device Found"}
                  </MenuItem>
                  {printers.map((p) => (
                    <MenuItem key={p.id} value={printerType === PRINTER_TYPES.DIRECT_USB ? p.path : p.name}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button className={styles.buttonRed} onClick={() => handleReloadPrinters(true)}>
                Reload
              </Button>
            </>
          )}
        </Grid>
      </Grid>

      {/* Barcode Format */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <FormatIcon />
          <h3 className={styles.subTitle}>Barcode Format :</h3>
          <FormControl variant="standard" className={styles.selectWrapper}>
            <Select
              value={barcodePrinterFormat}
              onChange={(e) => setBarcodePrinterFormat(e.target.value)}
              displayEmpty
              disableUnderline
              IconComponent={ExpandMoreIcon}
              renderValue={(value) =>
                value ? (
                  <span className={styles.mainTitle}>{value}</span>
                ) : (
                  <span style={{ color: "#aaa" }}>Select Barcode Format</span>
                )
              }
            >
              <MenuItem disabled value="">
                Select Barcode Format
              </MenuItem>
              {BARCODE_PRINTER.barcodeFormat.map((format) => (
                <MenuItem key={format} value={format}>
                  {format}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Barcode Column */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <PrinterIcon />
          <h3 className={styles.subTitle}>Barcode Column :</h3>
          <TextField
            placeholder="Enter barcode column..."
            value={barcodeColumn}
            onChange={(e) => setBarcodeColumn(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              "& .MuiInputBase-input": {
                padding: 0,
                fontSize: "14px",
                fontWeight: 600,
                color: "#2b2b2b",
                background: "transparent",
                border: "none",
                outline: "none",
              },
            }}
          />
        </Grid>
      </Grid>

      {/* Custom Note */}
      <Grid item xs={12} className={styles.titleWrapper}>
        <Grid className={styles.formSection}>
          <PrinterIcon />
          <h3 className={styles.subTitle}>Custom Note :</h3>
          <TextField
            placeholder="Enter note..."
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              "& .MuiInputBase-input": {
                padding: 0,
                fontSize: "14px",
                fontWeight: 600,
                color: "#2b2b2b",
                background: "transparent",
                border: "none",
                outline: "none",
              },
            }}
          />
        </Grid>
      </Grid>

      {/* Buttons */}
      <Grid item xs={12} display="flex" justifyContent="flex-end">
        <Button className={styles.buttonGray} onClick={handleTestPrint}>
          Test Printer
        </Button>
        <Button className={styles.buttonRed} onClick={handleSave}>
          Save
        </Button>
      </Grid>
    </Grid>
  );
}

export default BarcodePrintersPage;
